import PropTypes from 'prop-types';
import { placeholderImage, propertyHasRooms } from '../propertyConfig';
import {
  formatPrice,
  getPropertyArea,
  getPropertyAddress,
  getPropertyAltText,
  getPropertyImages,
  getPropertyState,
  getRelativeTimeAgo,
  formatShortDate,
  isTruthy,
} from '../propertyUtils';
import { useCurrency } from '../context/CurrencyContext';
import { MapPinIcon, TagIcon } from './IconSet';
import PropertyImageCarousel from './PropertyImageCarousel';

function ListingCard({ item, onOpenProperty }) {
  const currencyCtx = useCurrency();
  const isCardInteractive = typeof onOpenProperty === 'function';
  const cardImages = getPropertyImages(item, placeholderImage);
  const listingType = item.listing_type || item.type || 'rent';
  const approvalStatus = String(item.approval_status || item.approvalStatus || 'approved');
  const shouldShowApprovalBadge = approvalStatus !== 'approved';
  const approvalBadgeLabel =
    approvalStatus === 'pending'
      ? 'Pending review'
      : approvalStatus === 'rejected'
      ? 'Not approved'
      : approvalStatus.replace(/-/g, ' ');
  const isShortStay = listingType === 'short-let' || listingType === 'hotel';
  const availabilityStatus = item.availability_status || 'available';
  const isUnavailable = availabilityStatus !== 'available';
  const postedAgo = getRelativeTimeAgo(item.createdAt || item.created_at);

  const handleOpenProperty = () => {
    if (!isCardInteractive) {
      return;
    }
    onOpenProperty(item.id);
  };

  return (
    <article
      className={`listing-card${isUnavailable ? ' listing-card--unavailable' : ''}${
        isCardInteractive ? ' listing-card--clickable' : ''
      }`}
      onClick={isCardInteractive ? handleOpenProperty : undefined}
    >
      {isUnavailable && (
        <div
          className={`listing-card__status-overlay listing-card__status-overlay--${availabilityStatus}`}
        >
          <span className="listing-card__status-label">{availabilityStatus.toUpperCase()}</span>
        </div>
      )}
      <PropertyImageCarousel
        images={cardImages}
        fallbackImage={placeholderImage}
        alt={getPropertyAltText(item)}
      />
      <div className="listing-body">
        <h3>
          {isCardInteractive ? (
            <button
              className="listing-card__title-btn"
              onClick={handleOpenProperty}
              title={item.title}
            >
              {item.title}
            </button>
          ) : (
            item.title
          )}
        </h3>
        {shouldShowApprovalBadge && (
          <span className={`listing-approval-badge listing-approval-badge--${approvalStatus}`}>
            {approvalBadgeLabel}
          </span>
        )}
        {(item.agent_logo_url || item.agent_company_name) && (
          <div className="listing-card__agent-badge">
            {item.agent_logo_url && (
              <img
                src={item.agent_logo_url}
                alt=""
                className="listing-card__agent-logo"
                loading="lazy"
              />
            )}
            {item.agent_company_name && (
              <span className="listing-card__agent-name">{item.agent_company_name}</span>
            )}
          </div>
        )}
        <p className="price">
          {isShortStay && item.price_per_night ? (
            <>
              {formatPrice(item.price_per_night, currencyCtx)}
              <span className="price-unit"> / night</span>
            </>
          ) : (
            <>
              {formatPrice(item.price || 0, currencyCtx)}
              {listingType === 'rent' && <span className="price-unit"> / year</span>}
            </>
          )}
        </p>
        <div className="listing-meta-row">
          <p className="listing-meta-line">
            <MapPinIcon size={16} />
            {getPropertyAddress(item) ||
              `${getPropertyArea(item) || 'Unknown area'}, ${getPropertyState(item) || 'Nigeria'}`}
          </p>
          <p className="listing-meta-line">
            <TagIcon size={16} />
            {listingType} • {item.propertyType || item.property_type || 'Apartment'}
            {propertyHasRooms(item.propertyType || item.property_type) &&
              ` • Beds ${item.bedrooms || 'N/A'}`}
            {isShortStay && item.max_guests ? ` • ${item.max_guests} guests` : ''}
          </p>
        </div>
        <div className="tag-row">
          {isShortStay && (
            <span className="tag-highlight">{listingType === 'hotel' ? 'Hotel' : 'Short-let'}</span>
          )}
          {isTruthy(item.furnished) && <span>Furnished</span>}
          {isTruthy(item.serviced) && <span>Serviced</span>}
          {isTruthy(item.studentFriendly) && <span>Student-friendly</span>}
          {isShortStay && item.min_nights && (
            <span>
              Min {item.min_nights} night{item.min_nights > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {isShortStay && (item.availability_start || item.availability_end) && (
          <p className="listing-card__availability">
            Available {formatShortDate(item.availability_start) || 'now'}
            {item.availability_end ? ` – ${formatShortDate(item.availability_end)}` : ''}
          </p>
        )}
        {postedAgo && <p className="listing-card__posted">Posted {postedAgo}</p>}
      </div>
    </article>
  );
}

export default ListingCard;

ListingCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    createdAt: PropTypes.string,
    listing_type: PropTypes.string,
    type: PropTypes.string,
    availability_status: PropTypes.string,
    approval_status: PropTypes.string,
    approvalStatus: PropTypes.string,
    price_per_night: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    propertyType: PropTypes.string,
    property_type: PropTypes.string,
    bedrooms: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    max_guests: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    furnished: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
    serviced: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
    studentFriendly: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
    min_nights: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    availability_start: PropTypes.string,
    availability_end: PropTypes.string,
    posted_by: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    agent_company_name: PropTypes.string,
    agent_logo_url: PropTypes.string,
  }).isRequired,
  onOpenProperty: PropTypes.func,
};
